'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Globe, BookOpen, LayoutDashboard, LogIn, HelpCircle } from 'lucide-react';

export default function PublicHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [lang, setLang] = useState<'en' | 'el'>('en');

  const navItems = [
    { href: '/public-thesis-portal', label: lang === 'en' ? 'Browse Theses' : 'Περιήγηση Διατριβών', icon: BookOpen },
    { href: '/student-submission-portal', label: lang === 'en' ? 'Submit Thesis' : 'Υποβολή Διατριβής', icon: LayoutDashboard },
    { href: '/help', label: lang === 'en' ? 'Help' : 'Βοήθεια', icon: HelpCircle },
    { href: '/admin-management-panel', label: lang === 'en' ? 'Admin Panel' : 'Πίνακας Διαχείρισης', icon: LayoutDashboard },
  ];

  return (
    <>
      {/* Top utility bar */}
      <div className="bg-[#111e3d] text-white/70 text-xs py-1.5 px-6">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between">
          <span>Philips University — Academic Repository | Ακαδημαϊκό Αποθετήριο</span>
          <div className="flex items-center gap-4">
            <a href="tel:+35722441860" className="hover:text-[#c9a84c] transition-colors">+357 22 441860</a>
            <a href="mailto:library@philipsuni.ac.cy" className="hover:text-[#c9a84c] transition-colors">library@philipsuni.ac.cy</a>
          </div>
        </div>
      </div>
      {/* Main navbar */}
      <header className="bg-[#1a2e5a] shadow-lg sticky top-0 z-50">
        <div className="max-w-screen-2xl mx-auto px-6 xl:px-10 2xl:px-16">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link href="/public-thesis-portal" className="flex items-center gap-3 group">
              <AppLogo size={38} className="flex-shrink-0" />
              <div className="hidden sm:block">
                <div className="text-white font-700 text-base leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {lang === 'en' ? 'Philips University' : 'Πανεπιστήμιο Φίλιππος'}
                </div>
                <div className="text-[#c9a84c] text-xs font-500 leading-tight">
                  {lang === 'en' ? 'Thesis Repository' : 'Αποθετήριο Διατριβών'}
                </div>
              </div>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems?.map((item) => (
                <Link
                  key={`nav-${item?.href}`}
                  href={item?.href}
                  className="text-white/80 hover:text-white hover:bg-white/10 px-4 py-2 rounded-lg text-sm font-500 transition-all duration-150 flex items-center gap-2"
                >
                  {item?.label}
                </Link>
              ))}
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-2">
              {/* Language toggle */}
              <button
                onClick={() => setLang(lang === 'en' ? 'el' : 'en')}
                className="flex items-center gap-1.5 text-white/70 hover:text-white border border-white/20 hover:border-white/40 px-3 py-1.5 rounded-lg text-xs font-600 transition-all duration-150"
              >
                <Globe size={13} />
                {lang === 'en' ? 'ΕΛ' : 'EN'}
              </button>

              {/* Login button */}
              <Link
                href="/student-submission-portal"
                className="hidden sm:flex items-center gap-2 bg-[#c9a84c] hover:bg-[#a8892e] text-[#111e3d] font-600 px-4 py-2 rounded-lg text-sm transition-all duration-150 active:scale-95"
              >
                <LogIn size={14} />
                {lang === 'en' ? 'Student Login' : 'Σύνδεση Φοιτητή'}
              </Link>

              {/* Mobile hamburger */}
              <button
                className="md:hidden text-white/80 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-all"
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label="Toggle menu"
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="md:hidden border-t border-white/10 bg-[#111e3d] animate-fade-in">
            <div className="px-4 py-3 space-y-1">
              {navItems?.map((item) => (
                <Link
                  key={`mobile-nav-${item?.href}`}
                  href={item?.href}
                  className="flex items-center gap-3 text-white/80 hover:text-white hover:bg-white/10 px-3 py-2.5 rounded-lg text-sm font-500 transition-all"
                  onClick={() => setMobileOpen(false)}
                >
                  <item.icon size={16} />
                  {item?.label}
                </Link>
              ))}
              <Link
                href="/student-submission-portal"
                className="flex items-center gap-2 bg-[#c9a84c] text-[#111e3d] font-600 px-3 py-2.5 rounded-lg text-sm mt-2"
                onClick={() => setMobileOpen(false)}
              >
                <LogIn size={14} />
                {lang === 'en' ? 'Student Login' : 'Σύνδεση Φοιτητή'}
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
}